/**
 * 集計グラフの塗り (docs/27 §9-3、2026-09-16 オーナー指示)。
 *
 * - 項目の色は薄くする (濃いと隣の棒とぶつかって読みにくい)。ただし薄すぎると区別できないため、
 *   評価の区分ごとに濃さを変える。
 * - 評価の区分 (ポジ / ネガ / 中立) は模様 (ハッチング) でも区別する。色と模様の両方で分けることで、
 *   白黒印刷でも区別できる。
 * - **模様の線の色は評価の色に揃える** (ポジティブ = 緑 / ネガティブ = 赤 / 中立 = 灰、
 *   オーナー指示 2026-09-18)。画面の他の場所 (評価の内訳の +/−/○ の文字色、地図の色分け) が
 *   `POLARITY_DEFAULT_COLORS` を使っており、模様だけが項目の色から作られていて連動していなかった。
 * - **斜線は 1 種類だけにする** (同指示)。以前はポジティブ = 右上がり / ネガティブ = 右下がりの
 *   斜線で、向きの違いしか無く見分けにくかったため、ネガティブは縦線 + 横線の格子 (正方形) にする。
 * - 積み上げの区切りは白いスリット (境界線) で明確にする。
 *
 * 評価の極性のキーだけを見るので、テンプレートや地区に依存しない。
 */
import { POLARITY_DEFAULT_COLORS, type EvaluationPolarity } from '@citizen-walk/shared-types';

/** 16 進色を白と混ぜて薄くする (ratio=0 で元の色、1 で白)。 */
export function lightenHex(hex: string, ratio: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const value = parseInt(m[1]!, 16);
  const r = (value >> 16) & 0xff;
  const g = (value >> 8) & 0xff;
  const b = value & 0xff;
  const k = Math.min(1, Math.max(0, ratio));
  const mix = (c: number) => Math.round(c + (255 - c) * k);
  return `#${[mix(r), mix(g), mix(b)].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/** 評価なし (区分が付いていない分) の薄さ。 */
const PLAIN_LIGHTEN_RATIO = 0.55;

/**
 * 評価の区分ごとの塗りの濃さ (薄さの比率)。ネガティブ → ポジティブ → 中立 の順に薄くして、
 * 模様が見えにくい小さな棒でも濃淡で区別できるようにする。
 */
const POLARITY_LIGHTEN_RATIO: Record<EvaluationPolarity, number> = {
  negative: 0.5,
  positive: 0.6,
  neutral: 0.75,
};

/**
 * 評価の区分の表示順 (積み上げ・凡例・ツールチップ・PDF で共通、オーナー指示 2026-09-16)。
 * ポジティブ → 中立 → ネガティブ。
 */
export const POLARITY_DISPLAY_ORDER: readonly EvaluationPolarity[] = [
  'positive',
  'neutral',
  'negative',
];

/**
 * 評価の区分ごとの模様 (線の色は評価の色、2026-09-18)。
 *   - ポジティブ: 緑の斜線 (右上がり)
 *   - 中立: 灰色の水玉
 *   - ネガティブ: 赤の格子 (縦線 + 横線)
 */
export const POLARITY_HATCH_DESCRIPTIONS: Record<EvaluationPolarity, string> = {
  positive: '緑の斜線',
  neutral: '灰色の水玉',
  negative: '赤の格子',
};

/** 模様の線の色 (評価の色)。評価の定義に色があればそれを優先できるよう引数で差し替えられる。 */
export function polarityHatchColor(
  polarity: EvaluationPolarity,
  colors: Record<EvaluationPolarity, string> = POLARITY_DEFAULT_COLORS,
): string {
  return colors[polarity] ?? POLARITY_DEFAULT_COLORS[polarity];
}

export interface BarFill {
  /** `style` にそのまま渡せる背景 (模様)。 */
  background: string;
  /** 模様を描けない環境 (PDF 生成など) 向けのベタ塗り。 */
  backgroundColor: string;
}

/**
 * 項目の色と評価の区分から、棒の塗りを作る。
 * 模様は `repeating-linear-gradient` / `radial-gradient` で描く (画像を持たないので任意の色に追従する)。
 */
export function barFill(
  color: string,
  polarity?: EvaluationPolarity | null,
  /** 評価の色 (未指定なら既定の 緑 / 赤 / 灰)。 */
  polarityColors?: Record<EvaluationPolarity, string>,
): BarFill {
  if (!polarity) {
    const plain = lightenHex(color, PLAIN_LIGHTEN_RATIO);
    return { background: plain, backgroundColor: plain };
  }
  // 地色 = 項目 (またはエリア) の色を薄くしたもの。模様の線 = 評価の色。
  const base = lightenHex(color, POLARITY_LIGHTEN_RATIO[polarity]);
  const line = polarityHatchColor(polarity, polarityColors);
  if (polarity === 'neutral') {
    // 水玉 (斜線・格子と見間違えない模様にする)。
    return {
      background: `radial-gradient(${line} 1.5px, ${base} 1.6px)`,
      backgroundColor: base,
    };
  }
  if (polarity === 'negative') {
    // 格子 (縦線 + 横線)。斜線と形で見分けられるようにする (2026-09-18)。
    return {
      background: [
        `repeating-linear-gradient(0deg, ${line} 0 1px, transparent 1px 6px)`,
        `repeating-linear-gradient(90deg, ${line} 0 1px, transparent 1px 6px)`,
        base,
      ].join(', '),
      backgroundColor: base,
    };
  }
  // ポジティブ: 右上がりの斜線。
  return {
    background: `repeating-linear-gradient(45deg, ${line} 0 2px, ${base} 2px 7px)`,
    backgroundColor: base,
  };
}

/**
 * 模様に必要な背景サイズ (`backgroundSize`)。水玉は 1 粒の間隔、格子は升目の大きさを決める。
 * 斜線 (`repeating-linear-gradient` の繰り返し) は指定不要。
 */
export function barFillBackgroundSize(polarity?: EvaluationPolarity | null): string | undefined {
  if (polarity === 'neutral') return '6px 6px';
  if (polarity === 'negative') return '6px 6px';
  return undefined;
}

/** 積み上げの区切り (白いスリット)。隣の区分との境目をはっきりさせる。 */
export const STACK_SEPARATOR_STYLE = {
  boxShadow: 'inset -1px 0 0 #ffffff',
} as const;
